import * as React from "react";

/**
 * The "looks pressed" flag that `:active` cannot provide.
 *
 * `:active` fires for a mouse press and NOT for a keyboard one, so a control activated with
 * Enter or Space looks completely inert while it is being used. Every clickable control in this
 * design system therefore tracks the pressed look by hand.
 *
 * The timeout is the load-bearing part and it looks like a mistake. A key-down sets `active`;
 * the effect then schedules a check on the next tick that clears it UNLESS a key is still held.
 * That is what stops a click leaving the control stuck in its pressed paint when no key-up
 * follows — a pointer press whose release lands outside the element, for instance.
 *
 * `Button` and `InteractableCard` each still carry their own inline copy of this logic. Theirs
 * differ in ways that matter to them (Button folds in `loading` and `focusableWhenDisabled`),
 * so they are not converted here; converging all three is tracked separately rather than done
 * as a silent side effect of porting Pill.
 */
export function usePressedState({
  disabled,
  onBlur,
  onClick,
  onKeyDown,
  onKeyUp,
}: {
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLElement>) => void;
}) {
  const [keyIsDown, setKeyIsDown] = React.useState("");
  const [active, setActive] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (keyIsDown !== "Enter" && keyIsDown !== " ") setActive(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [active, keyIsDown]);

  return {
    active,
    handlers: {
      onBlur: (event: React.FocusEvent<HTMLElement>) => {
        setActive(false);
        onBlur?.(event);
      },
      onClick: disabled
        ? undefined
        : (event: React.MouseEvent<HTMLElement>) => {
            setActive(true);
            onClick?.(event);
          },
      onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          setKeyIsDown(event.key);
          setActive(true);
        }
        onKeyDown?.(event);
      },
      onKeyUp: (event: React.KeyboardEvent<HTMLElement>) => {
        setKeyIsDown("");
        setActive(false);
        onKeyUp?.(event);
      },
    },
  };
}
